import dayjs from 'dayjs';
import { isArray as _isArray } from 'lodash-es';

import {
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS,
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY,
  LOCAL_FILTERS_KEY_GLOBAL_BENCHMARKING,
  LOCAL_FILTERS_KEY_AGGREGATED_CONSENT,
  LOCAL_FILTERS_KEY_PARTNER_SCORE,
  LOCAL_FILTERS_KEY_COMPETITOR_ANALYSIS,
  LOCAL_FILTERS_KEY_INSIGHTS,
} from '@/context/LocalFiltersRegistry/localFiltersKeys';
import { AdditionalChannelTabs, Channel, FILTER_CONFIG, FilterType, type FilterValue } from '@/models';
import Stars from '@/components/shared/rating/Stars/Stars';
import { Period, TimePeriod } from '@/types/date';
import { isFilterType } from '@/utils/filter/isFilterType';
import { isRecord } from '@/utils/filter/isRecord';
import { AppRoutes } from '@/utils';
import { formatPeriodShortWithYear } from '@/utils/date';
import { type ChannelGroup, type LocalFilterSection } from '@/storybook/components/FilterSetPreview/FilterSetPreview';
import { type FilterChipItem } from '@/storybook/components/FilterSetPreview/FilterSetPreview';
import { FilterState } from '@/types/filter';
import { collapseTreeSelectValues } from '@/utils/filter/collapseTreeSelectValues';

/** Sections whose local filters are nested by channel (e.g. channelFocus.email.someFilter). */
export const CHANNEL_NESTED_KEYS = new Set([
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS,
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY,
  LOCAL_FILTERS_KEY_GLOBAL_BENCHMARKING,
]);

/** Maps local-filter registry keys to their i18n navigation label keys. */
export const SECTION_LABEL_MAP: Record<string, string> = {
  [LOCAL_FILTERS_KEY_CHANNEL_FOCUS]: 'header.navigation.channelFocus',
  [LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY]: 'main.channelFocus.evolution.filters.secondarySegment.label',
  [LOCAL_FILTERS_KEY_GLOBAL_BENCHMARKING]: 'header.navigation.globalBenchmarking',
  [LOCAL_FILTERS_KEY_AGGREGATED_CONSENT]: 'header.navigation.aggregatedConsent',
  [LOCAL_FILTERS_KEY_PARTNER_SCORE]: 'header.navigation.partnerScore',
  [LOCAL_FILTERS_KEY_COMPETITOR_ANALYSIS]: 'header.navigation.competitorAnalysis',
  [LOCAL_FILTERS_KEY_INSIGHTS]: 'header.navigation.insights',
};

/** Returns a translated display label for a channel tab key. */
export const getChannelLabel = (key: string, t: (k: string) => string): string => {
  const channelLabelKeys: Record<string, string> = {
    [AdditionalChannelTabs.ALL_CHANNEL]: 'global.filters.channels.allChannels',
    [Channel.VISITS_1_TO_1]: 'global.filters.channels.oneToOneVisit',
    [Channel.EMAIL]: 'global.filters.channels.email',
    [Channel.EVENT]: 'global.filters.channels.event',
    [Channel.WEBSITE]: 'global.filters.channels.website',
  };
  const translationKey = channelLabelKeys[key];
  return translationKey ? t(translationKey) : key;
};

/** Maps AppRoutes group prefixes to local-filter registry keys. */
export const ROUTE_TO_SECTION_KEY: Record<string, string> = {
  [AppRoutes.CHANNEL_FOCUS_GROUP]: LOCAL_FILTERS_KEY_CHANNEL_FOCUS,
  [AppRoutes.GLOBAL_BENCHMARKING_GROUP]: LOCAL_FILTERS_KEY_GLOBAL_BENCHMARKING,
  [AppRoutes.AGGREGATED_CONSENT_GROUP]: LOCAL_FILTERS_KEY_AGGREGATED_CONSENT,
  [AppRoutes.PARTNERSCORE_GROUP]: LOCAL_FILTERS_KEY_PARTNER_SCORE,
  [AppRoutes.COMPETITOR_ANALYSIS_GROUP]: LOCAL_FILTERS_KEY_COMPETITOR_ANALYSIS,
  [AppRoutes.INSIGHTS_GROUP]: LOCAL_FILTERS_KEY_INSIGHTS,
};

/** Route prefixes whose sub-paths identify a channel tab. */
export const CHANNEL_ROUTE_PREFIXES: ReadonlySet<string> = new Set([
  AppRoutes.CHANNEL_FOCUS_GROUP,
  AppRoutes.GLOBAL_BENCHMARKING_GROUP,
  AppRoutes.AGGREGATED_CONSENT_GROUP,
]);

/** Extracts the last path segment from an AppRoutes value (e.g. '/channel-focus/all-channels' → 'all-channels'). */
const lastSegment = (route: string): string => route.split('/').pop() ?? '';

/**
 * Maps URL path segments (after the group prefix) to Channel / AdditionalChannelTabs enum values.
 * Includes both singular and plural variants for routes like /channel-focus/1to1visit.
 */
export const ROUTE_CHANNEL_MAP: Record<string, string> = {
  [lastSegment(AppRoutes.CHANNEL_ALL_CHANNELS)]: AdditionalChannelTabs.ALL_CHANNEL,
  [lastSegment(AppRoutes.CHANNEL_FOCUS_1_TO_1)]: Channel.VISITS_1_TO_1,
  [Channel.VISITS_1_TO_1]: Channel.VISITS_1_TO_1,
  [Channel.EMAIL]: Channel.EMAIL,
  [Channel.EVENT]: Channel.EVENT,
  [Channel.WEBSITE]: Channel.WEBSITE,
};

/** Extracts the group prefix from a pathname (e.g. '/channel-focus/email' → '/channel-focus'). */
const getGroupPrefix = (pathname: string): string => {
  const secondSlash = pathname.indexOf('/', 1);
  return secondSlash === -1 ? pathname : pathname.slice(0, secondSlash);
};

export const getActiveSectionKey = (pathname: string): string | null =>
  ROUTE_TO_SECTION_KEY[getGroupPrefix(pathname)] ?? null;

export const getActiveChannelKey = (pathname: string): string | undefined => {
  const prefix = getGroupPrefix(pathname);
  if (!CHANNEL_ROUTE_PREFIXES.has(prefix)) return undefined;
  const segment = pathname.slice(prefix.length + 1);
  return ROUTE_CHANNEL_MAP[segment] ?? (segment || undefined);
};

export const isValidPeriod = (value: unknown): value is Period => {
  if (typeof value !== 'object' || value === null) return false;
  return 'beginn' in value && 'end' in value && 'period' in value;
};

export const toDayjs = (value: unknown): dayjs.Dayjs => {
  if (dayjs.isDayjs(value)) return value;
  if (typeof value === 'string') return dayjs(value);
  return dayjs();
};

export const buildDateRangeText = (
  dateRange: Period,
  comparisonPeriod: Period | undefined,
  t: (key: string) => string,
): string => {
  const rangeText = formatPeriodShortWithYear(dateRange);
  if (!comparisonPeriod) return rangeText;

  const periodLabelMap: Partial<Record<TimePeriod, string>> = {
    [TimePeriod.WEEK]: t('header.navigation.dateRange.previousWeek'),
    [TimePeriod.MONTH]: t('header.navigation.dateRange.previousMonth'),
    [TimePeriod.QUARTER]: t('header.navigation.dateRange.previousQuarter'),
    [TimePeriod.YTD]: t('header.navigation.dateRange.previousYTD'),
    [TimePeriod.YEAR]: t('header.navigation.dateRange.previousYear'),
    [TimePeriod.CYCLE]: t('header.navigation.dateRange.previousCycle'),
  };

  const comparisonText = periodLabelMap[comparisonPeriod.period] ?? `vs ${formatPeriodShortWithYear(comparisonPeriod)}`;

  return `${rangeText} ${comparisonText}`;
};

export const flattenLocalChips = (data: Record<string, unknown>): FilterChipItem[] =>
  Object.entries(data).flatMap(([filterKey, filterValue]) => {
    if (!Array.isArray(filterValue)) return [];
    const stringValues = filterValue.filter((item): item is string => typeof item === 'string');
    return collapseTreeSelectValues(stringValues).map(group => ({
      key: `${filterKey}-${group.originalValues[0]}`,
      label: group.label,
    }));
  });

export const buildLocalSections = (
  localFilters: Record<string, unknown>,
  t: (key: string) => string,
): LocalFilterSection[] =>
  Object.entries(localFilters)
    .map(([sectionKey, sectionData]): LocalFilterSection | null => {
      if (!isRecord(sectionData)) return null;

      const translationKey = SECTION_LABEL_MAP[sectionKey];
      const sectionLabel = translationKey ? t(translationKey) : sectionKey;

      if (CHANNEL_NESTED_KEYS.has(sectionKey)) {
        const channelGroups = Object.entries(sectionData)
          .map(([channelKey, channelFilters]) => {
            if (!isRecord(channelFilters)) return null;
            const chips = flattenLocalChips(channelFilters);
            if (chips.length === 0) return null;
            return {
              channelKey,
              channelLabel: getChannelLabel(channelKey, t),
              chips,
            };
          })
          .filter((group): group is ChannelGroup => group != null);

        if (channelGroups.length === 0) return null;
        return { sectionKey, sectionLabel, channelGroups };
      }

      const chips = flattenLocalChips(sectionData);
      if (chips.length === 0) return null;
      return { sectionKey, sectionLabel, chips };
    })
    .filter((section): section is LocalFilterSection => section != null);

const DATE_FILTER_KEYS = new Set<string>([FilterType.DATE_RANGE, FilterType.COMPARISON_PERIOD, FilterType.SINGLE_DATE]);

const chipKey = (filterKey: string, value: FilterValue): string => {
  if (value === null || value === undefined) return filterKey;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return `${filterKey}-${String(value.value)}`;
  }
  return `${filterKey}-${String(value)}`;
};

const isSelectItemWithLevel = (value: unknown): value is { value: unknown; label: string; level: number } =>
  isRecord(value) && 'value' in value && 'level' in value && typeof value.level === 'number';

export const buildGlobalChips = (filterState: FilterState, t: (key: string) => string): FilterChipItem[] => {
  const chips: FilterChipItem[] = [];

  for (const [filterKey, filterValue] of Object.entries(filterState)) {
    if (DATE_FILTER_KEYS.has(filterKey) || filterValue == null || filterValue === false) continue;

    if (!isFilterType(filterKey)) continue;
    const config = FILTER_CONFIG[filterKey];
    const values: FilterValue[] = _isArray(filterValue) ? filterValue : [filterValue];

    for (const singleValue of values) {
      if (isSelectItemWithLevel(singleValue)) {
        const displayLabel = typeof singleValue.label === 'string' ? singleValue.label : String(singleValue.value);
        chips.push({
          key: chipKey(filterKey, singleValue),
          label: displayLabel,
          content: <Stars value={singleValue.level} />,
        });
        continue;
      }

      const raw = config?.format?.({ value: singleValue, t, contexts: { userCountries: [] } });
      chips.push({
        key: chipKey(filterKey, singleValue),
        label: typeof raw === 'string' ? raw : String(singleValue),
      });
    }
  }

  return chips;
};

export const parseDateRange = (filterState: FilterState, t: (key: string) => string): string | undefined => {
  const dateRangeValue = filterState[FilterType.DATE_RANGE];
  const comparisonValue = filterState[FilterType.COMPARISON_PERIOD];
  if (!isValidPeriod(dateRangeValue)) return undefined;

  const dateRange: Period = {
    beginn: toDayjs(dateRangeValue.beginn),
    end: toDayjs(dateRangeValue.end),
    period: dateRangeValue.period,
  };
  const comparison = isValidPeriod(comparisonValue)
    ? { beginn: toDayjs(comparisonValue.beginn), end: toDayjs(comparisonValue.end), period: comparisonValue.period }
    : undefined;

  return buildDateRangeText(dateRange, comparison, t);
};
